import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Modal from 'layout/atoms/Modal';
import { AppDispatch } from 'store';
import { closeModal } from 'store/layoutSlice';
import { fetchTaskNotes, saveTaskNotes } from 'store/tasksThunks';
import { selectTask } from 'store/workspaceSlice';

import styles from './TaskNotesModal.module.scss';

const AUTOSAVE_DEBOUNCE_MS = 2000;

type SaveStatus = 'idle' | 'unsaved' | 'saving' | 'saved';

type TaskNotesModalProps = {
    taskId?: string;
};

const TaskNotesModal = ({ taskId }: TaskNotesModalProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { t } = useTranslation();
    const task = useSelector((state: any) => selectTask(state, taskId));

    const [content, setContent] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [status, setStatus] = useState<SaveStatus>('idle');

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // What is currently persisted on the server, so we never save a no-op.
    const savedContentRef = useRef<string>('');

    const persist = useCallback(
        (value: string) => {
            if (!taskId || value === savedContentRef.current) {
                setStatus((s) => (s === 'unsaved' ? 'saved' : s));
                return;
            }
            setStatus('saving');
            dispatch(saveTaskNotes({ taskId, content: value }))
                .unwrap()
                .then(() => {
                    savedContentRef.current = value;
                    setStatus('saved');
                })
                .catch(() => setStatus('unsaved'));
        },
        [dispatch, taskId]
    );

    // Load the existing note when the dialog opens.
    useEffect(() => {
        if (!taskId) {
            return;
        }
        let cancelled = false;
        setLoading(true);
        dispatch(fetchTaskNotes({ taskId }))
            .unwrap()
            .then((payload: any) => {
                if (cancelled) {
                    return;
                }
                const loaded = payload?.response?.content || '';
                savedContentRef.current = loaded;
                setContent(loaded);
                setLoading(false);
            })
            .catch(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [dispatch, taskId]);

    // Focus once the note is loaded, caret at the end.
    useEffect(() => {
        if (!loading && textareaRef.current) {
            const el = textareaRef.current;
            el.focus();
            el.setSelectionRange(el.value.length, el.value.length);
        }
    }, [loading]);

    const handleChange = (value: string) => {
        setContent(value);
        setStatus('unsaved');

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(
            () => persist(value),
            AUTOSAVE_DEBOUNCE_MS
        );
    };

    // Flush any pending edit when the dialog goes away, so closing early
    // never loses what was typed in the last two seconds.
    const contentRef = useRef<string>(content);
    contentRef.current = content;

    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
                persist(contentRef.current);
            }
        };
    }, [persist]);

    const handleClose = () => {
        dispatch(closeModal({ name: 'taskNotes' }));
    };

    const statusLabel = () => {
        switch (status) {
            case 'saving':
                return t('TASK.NOTES.SAVING');
            case 'saved':
                return t('TASK.NOTES.SAVED');
            case 'unsaved':
                return t('TASK.NOTES.UNSAVED');
            default:
                return '';
        }
    };

    return (
        <Modal open={true} size="xl" onClose={handleClose}>
            <div className={styles.wrap}>
                <header className={styles.header}>
                    <div className={styles.titles}>
                        <h2 className={styles.title}>
                            {t('TASK.NOTES.TITLE')}
                        </h2>
                        {task?.name && (
                            <span className={styles.taskName}>{task.name}</span>
                        )}
                    </div>
                </header>

                <textarea
                    ref={textareaRef}
                    className={styles.textarea}
                    value={content}
                    disabled={loading}
                    placeholder={t('TASK.NOTES.PLACEHOLDER')}
                    onChange={(e) => handleChange(e.target.value)}
                />

                <footer className={styles.footer}>
                    <span className={styles.status}>{statusLabel()}</span>
                    <span className={styles.hint}>
                        {t('TASK.NOTES.AUTOSAVE_HINT')}
                    </span>
                </footer>
            </div>
        </Modal>
    );
};

export default TaskNotesModal;
